import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { UseOutletContextRecurringContract } from './RecurringContract';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { RecurringContractDA } from '../../../../services/admin/recurring.contract.service';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { formatContact, formatMoney } from '../../../../scripts/format.value.script';
import { getError } from '../../../../languages/translations/response';
import { sortColumn } from '../../../../scripts/table.script';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { usePaginationUrl } from '../../../../hooks/usePaginationUrl';

import AppSortTable from '../../../../components/element/SortTable';
import AppPagination from '../../../../components/element/Pagination';
import AppPreloader from '../../../../components/Preloader';

export interface RecurringContractListPageProps {};

let errorResponse: Error;

const RecurringContractListPage: React.FunctionComponent<RecurringContractListPageProps> = props => {
  const {setRoute, companyForUser} = UseOutletContextRecurringContract()
  const {lang} = StateLanguage()
  const { page: urlPage, setPage: setUrlPage } = usePaginationUrl()

  const [mounted, setMounted] = useState(false)
  const [filterState, setFilterState] = useState({status: '', contact: '', sort_field: '', sort_mode: '', search: ''})
  const filterRef = useRef(filterState)
  const filter = filterRef.current
  const setFilter = (v: React.SetStateAction<typeof filterState>) => { const n = typeof v === 'function' ? v(filterRef.current) : v; filterRef.current = n; setFilterState(n) }
  const [pagination, setPagination] = useState({current: 1, limit: 20, total: 0, pages: [1] as number[]})
  const [contracts, setContracts] = useState<any[] | null>(null)

  const loadContracts = async (id_company: string, page: number = 1) => {
    const f = filterRef.current
    try {
      const response = await RecurringContractDA.getRecurringContracts(id_company, f.status, f.contact, f.sort_field, f.sort_mode, page, pagination.limit, f.search)
      if (response.status === 200) {
        const data = response.data
        const items = data?.items ?? (Array.isArray(data) ? data : [])

        const filterData = {
          company_id: id_company,
          status: f.status,
          contact: f.contact,
          sort_field: f.sort_field,
          sort_mode: f.sort_mode,
          search: f.search
        }
        localStorage.setItem('filter_recurring_contracts', JSON.stringify(filterData))

        setContracts(items)

        const paginationData = data?.pagination
        if (paginationData) {
          const totalPages = paginationData.pages || 1
          const clampedPage = page > totalPages && totalPages >= 1 ? totalPages : paginationData.page || page
          let pagesOfPagination: number[] = []
          for (let i = 1; i <= totalPages; i++) { pagesOfPagination.push(i) }
          setPagination({...pagination, current: clampedPage, total: paginationData.total || 0, pages: pagesOfPagination})
          setUrlPage(clampedPage)
          if (page > totalPages && totalPages >= 1) {
            loadContracts(id_company, totalPages)
            return
          }
        }
      } else {
        errorResponse = response.data
        setContracts([])
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      setContracts([])
      errorWebHandler(lang, error)
    }
  }

  const executeSortList = (item: string) => {
    if (companyForUser) {
      const newFilter = {...filterRef.current}
      sortColumn(item, newFilter)
      setFilter(newFilter)
      setUrlPage(1)
      loadContracts(companyForUser.id, 1)
    }
  }

  const loadPagination = (current: number) => {
    if (!companyForUser) return null
    setUrlPage(current)
    loadContracts(companyForUser.id, current)
  }

  const executeSearch = (value: string) => {
    setFilter(prev => ({...prev, search: value}))
    if (companyForUser) {
      filterRef.current = {...filterRef.current, search: value}
      setUrlPage(1)
      loadContracts(companyForUser.id, 1)
    }
  }

  const executeFilterStatus = (value: string) => {
    setFilter(prev => ({...prev, status: value}))
    if (companyForUser) {
      filterRef.current = {...filterRef.current, status: value}
      setUrlPage(1)
      loadContracts(companyForUser.id, 1)
    }
  }

  const statusColor = (status: string): string => {
    switch (status) {
      case 'active': return 'success'
      case 'paused': return 'warning'
      case 'ended': return 'secondary'
      case 'error': return 'danger'
      default: return 'secondary'
    }
  }

  const statusLabel = (status: string): string => {
    switch (status) {
      case 'active': return lang.labels.active
      case 'paused': return lang.labels.paused || 'Paused'
      case 'ended': return lang.labels.ended || 'Ended'
      case 'error': return lang.labels.error || 'Error'
      default: return status
    }
  }

  const cadenceLabel = (cadence: any): string => {
    if (!cadence) return '-'
    const { unit, interval } = cadence
    if (interval === 1) {
      switch (unit) {
        case 'month': return lang.labels.monthly || 'Monthly'
        case 'quarter': return lang.labels.quarterly || 'Quarterly'
        case 'year': return lang.labels.annual || 'Annual'
        default: return unit
      }
    }
    const unitLabel = unit === 'month' ? (lang.labels.months || 'months')
      : unit === 'quarter' ? (lang.labels.quarters || 'quarters')
      : (lang.labels.years || 'years')
    return `${lang.labels.every || 'Every'} ${interval} ${unitLabel}`
  }

  const contractTotal = (item: any): number => {
    const reqs = item.template?.requirements ?? []
    let total = 0
    for (const req of reqs) {
      for (const content of (req.contents ?? [])) {
        const qty = content.quantity ?? 0
        const price = content.price_unit ?? 0
        const lineHt = qty * price
        const tax = lineHt * (content.tax_rate ?? 0)
        total += content.tax_included ? lineHt : lineHt + tax
      }
    }
    return total
  }

  useEffect( () => {
    let filter_rc: any = (localStorage.getItem('filter_recurring_contracts')) ? JSON.parse(localStorage.getItem('filter_recurring_contracts')!) : null

    if (companyForUser) {
      const isMatch = filter_rc && filter_rc.company_id === companyForUser.id
      setFilter({
        status: isMatch ? (filter_rc.status ?? '') : '',
        contact: isMatch ? (filter_rc.contact ?? '') : '',
        sort_field: isMatch ? (filter_rc.sort_field ?? '') : '',
        sort_mode: isMatch ? (filter_rc.sort_mode ?? '') : '',
        search: isMatch ? (filter_rc.search ?? '') : ''
      })

      setContracts(null)
      loadContracts(companyForUser.id, urlPage)
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.recurringContracts || 'Recurring contracts', branch: lang.labels.list}, company: true})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && contracts
        ?
        <div className="card card-flush">
          <div className="card-header align-items-center py-5 gap-2 gap-md-5">
            <div className="card-title">
              <div className="d-flex align-items-center position-relative my-1">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input type="text" className="form-control w-250px ps-14" placeholder={lang.labels.searchRecurringContract || 'Search contract...'} value={filter.search} onChange={(e) => executeSearch(e.target.value)} />
              </div>
            </div>
            <div className="card-toolbar flex-row-fluid justify-content-end gap-3">
              <select className="form-select w-auto" value={filter.status} onChange={(e) => executeFilterStatus(e.target.value)}>
                <option value="">{lang.labels.allStatuses || 'All statuses'}</option>
                <option value="active">{lang.labels.active}</option>
                <option value="paused">{lang.labels.paused || 'Paused'}</option>
                <option value="ended">{lang.labels.ended || 'Ended'}</option>
                <option value="error">{lang.labels.error || 'Error'}</option>
              </select>
              <Link to="/main/sale/recurring-contract/create" className="btn btn-primary">
                {lang.labels.addRecurringContract || 'Add recurring contract'}
              </Link>
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="dataTables_wrapper">
              <div className="table-responsive">
                <table className="table align-middle table-row-dashed mb-2">
                  <thead className="border-bottom">
                    <tr className="text-start text-muted text-uppercase fw-bolder fs-7">
                      <th className="min-w-100px">
                        <span>{lang.labels.number}</span>
                      </th>
                      <th className="min-w-150px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("label")}>
                          <span className="me-1">{lang.labels.title || 'Title'}</span>
                          <AppSortTable attribute={"label"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <span>{lang.labels.customer}</span>
                      </th>
                      <th className="min-w-100px">
                        <span>{lang.labels.frequency || 'Frequency'}</span>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("next_issue_date")}>
                          <span className="me-1">{lang.labels.nextInvoiceDate || 'Next invoice'}</span>
                          <AppSortTable attribute={"next_issue_date"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px">
                        <div className="d-flex align-items-center text-nowrap cursor-pointer" onClick={() => executeSortList("status")}>
                          <span className="me-1">{lang.labels.status}</span>
                          <AppSortTable attribute={"status"} field={filter.sort_field} mode={filter.sort_mode}></AppSortTable>
                        </div>
                      </th>
                      <th className="min-w-100px text-end">
                        <span>{lang.labels.amountTotal}</span>
                      </th>
                    </tr>
                  </thead>
                  { contracts.length > 0
                    ?
                    <tbody className="text-secondary fw-bold">
                      { contracts.map (( (item, index) => { return (
                        <tr key={index} className="row-dashed">
                          <td>
                            <Link to={"/main/sale/recurring-contract/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.contract_number || '-'}</Link>
                          </td>
                          <td>
                            <Link to={"/main/sale/recurring-contract/view/" + item.id} className="fw-bolder text-nowrap text-dark text-hover-primary">{item.label ?? item.template?.concerning ?? '-'}</Link>
                          </td>
                          <td>{item.contact ? formatContact(item.contact) : '-'}</td>
                          <td>{cadenceLabel(item.cadence)}</td>
                          <td className="text-nowrap">{item.next_issue_date ? dateFormat(lang.code, item.next_issue_date) : '-'}</td>
                          <td>
                            <span className={`badge badge-light-${statusColor(item.status)} fs-7`}>{statusLabel(item.status)}</span>
                          </td>
                          <td className="text-end text-nowrap">
                            {item.template?.currency?.code ?? 'CHF'} {formatMoney(contractTotal(item))}
                          </td>
                        </tr>
                      )}))}
                    </tbody>
                    :
                    <tbody>
                      <tr className="text-center">
                        <td colSpan={7}>
                          <div className="d-flex flex-column text-center p-10">
                            <span className="text-dark fw-bolder fs-6">{lang.labels.noRecords}</span>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  }
                </table>
              </div>
              { pagination.pages.length > 1 &&
                <AppPagination pagination={pagination} loadPagination={loadPagination}></AppPagination>
              }
            </div>
          </div>
        </div>
        :
        <AppPreloader/>
      }
    </div>
  );
}

export default RecurringContractListPage;
